import { 
  Tabs, 
  Tab, 
  Container, 
  Paper, 
  Box, 
  Typography,
  Breadcrumbs,
  Link
} from '@mui/material'
import { Home, Notifications } from '@mui/icons-material'
import React from 'react'

import {
  NotificationMessageTemplate,
  NotificationSetupTemplate
} from '../../services/Hub/notificationCenter/types'
import { HubApi } from '../../services/HubApi'
import { WorkflowDTO } from '../../services/Hub/Workflow'
import { FieldDefinition, SelectOption } from '../../shared/components/AdvancedExpressionBuilder/types'
import { RESOURCE } from '../../services/Hub/Authorization/authorization.constants'
import CanonicalNotificationsTab from './components/CanonicalNotificationsTab'
import MappingTab from './components/MappingTab'
import TemplatesTab from './components/TemplatesTab'

type Props = {
  selectedTab: number
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void
  setupTemplates: NotificationSetupTemplate[]
  messageTemplates: NotificationMessageTemplate[]
  isLoading: boolean
  error: string | null
}

const NotificationCatalogView: React.FC<Props> = ({
  selectedTab,
  onTabChange,
  setupTemplates,
  messageTemplates,
  isLoading,
  error
}) => {
  // Implementation of fetchFieldCatalog using the trigger source field mapping
  const fetchFieldCatalog = async (search?: string): Promise<FieldDefinition[]> => {
    // Get trigger sources from multiple sources
    const triggerSourcesFromTemplates = [...new Set(
      setupTemplates
        .map(template => template.source)
        .filter(Boolean)
    )];

    // Try to get additional trigger sources from API
    let triggerSourcesFromAPI: string[] = [];
    try {
      const { Notifications } = await import('../../services/Hub/notificationCenter/notificationCenterApi');
      const triggerData = await Notifications.triggers.sources();
      triggerSourcesFromAPI = [...new Set([
        ...triggerData.eventsByTriggerSources.map((item: any) => item.triggerSource || ''),
        ...triggerData.triggerSourcesByEvents.map((item: any) => item.event || '')
      ])].filter(Boolean);
    } catch (error) {
      console.warn('Could not fetch trigger sources from API:', error);
    }

    // Combine all trigger sources, including well-known ones from field mapping
    const knownTriggerSources = [
      'TASK_CREATED',
      'TASK_UPDATED', 
      'TASK_COMPLETED',
      'TICKET_CREATED',
      'TICKET_UPDATED',
      'TICKET_TRANSITION_CREATED',
      'TICKET_COMMENT_CREATED',
      'DOCUMENT_FORMALIZATION_TICKET_CREATED',
      'DOCUMENT_FORMALIZATION_TICKET_UPDATED',
      'DOCUMENT_FORMALIZATION_TICKET_TRANSITION_CREATED',
      'DOCUMENT_SIGNATURE_COMPLETED',
      'DOCUMENT_SIGNATURE_CANCELED',
      'COLLATERAL_ASSETS_CREATED',
      'COLLATERAL_ASSETS_UPDATED',
      'AGMT_DOCUMENT_REGISTER_STATUS_CHANGED'
    ];

    const allTriggerSources = [...new Set([
      ...triggerSourcesFromTemplates,
      ...triggerSourcesFromAPI,
      ...knownTriggerSources
    ])];

    // Generate basic fields for all trigger sources
    const allFields: FieldDefinition[] = [
      // Common fields for all trigger sources
      { identifier: 'id', label: 'ID', fieldType: 'text' },
      { identifier: 'createdAt', label: 'Created Date', fieldType: 'date' },
      { identifier: 'updatedAt', label: 'Updated Date', fieldType: 'date' },
      { identifier: 'status', label: 'Status', fieldType: 'select' },
      { identifier: 'name', label: 'Name', fieldType: 'text' },
      { identifier: 'description', label: 'Description', fieldType: 'text' },
      { identifier: 'type', label: 'Type', fieldType: 'select' },
      { identifier: 'value', label: 'Value', fieldType: 'numeric' },
      { identifier: 'amount', label: 'Amount', fieldType: 'currency' },
      { identifier: 'userId', label: 'User ID', fieldType: 'select', dynamicOptions: {
        resource: RESOURCE['CRM:Memberships'],
        mapValue: '{{user.id}}',
        mapLabel: '{{user.name}} {{user.lastName}}',
        groupBy: '{{user.role}}',
      }},
      { identifier: 'workflowId', label: 'Workflow ID', fieldType: 'select', dynamicOptions: {
        resource: RESOURCE['CRM:Workflows'],
        mapValue: '{{id}}',
        mapLabel: '{{title}}',
        groupBy: '{{type}}',
      }},
      { identifier: 'customerId', label: 'Customer ID', fieldType: 'select', dynamicOptions: {
        resource: RESOURCE['CRM:Memberships'],
        mapValue: '{{customer.id}}',
        mapLabel: '{{customer.name}}',
        groupBy: '{{customer.type}}',
      }},
      { identifier: 'ticketId', label: 'Ticket ID', fieldType: 'select', dynamicOptions: {
        resource: RESOURCE['CRM:Workflows'],
        mapValue: '{{id}}',
        mapLabel: '{{title}}',
        groupBy: '{{type}}',
      }},
    ];

    // Remove duplicates by identifier and handle duplicate labels
    const uniqueFields = allFields.reduce((acc, field) => {
      const existing = acc.find(f => f.identifier === field.identifier);
      if (!existing) {
        // Check if label already exists and make it unique if needed
        const labelExists = acc.find(f => f.label === field.label);
        if (labelExists) {
          // Create a unique label by appending the identifier
          field = {
            ...field,
            label: `${field.label} (${field.identifier})`
          };
        }
        acc.push(field);
      }
      return acc;
    }, [] as FieldDefinition[]);

    // Filter by search term if provided
    if (search) {
      return uniqueFields.filter(field => 
        field.label.toLowerCase().includes(search.toLowerCase()) ||
        field.identifier.toLowerCase().includes(search.toLowerCase())
      );
    }

    return uniqueFields;
  };

  // Implementation of fetchResourceValues - this would typically connect to actual APIs
  const fetchResourceValues = async (resourceId: string, search?: string): Promise<SelectOption[]> => {
    try {
      // Handle different resource types
      switch (resourceId) {
        case 'WORKFLOW':
          // Fetch workflows from Hub API
          const workflows = await HubApi.Workflow.getWorkflows({
            organizationId: '3213', // Default org ID
            search,
            limit: 50 // Limit results for better performance
          });
          
          return workflows.map((workflow: WorkflowDTO) => ({
            label: workflow.title,
            value: workflow.id
          }));
          
        case 'USER':
          // TODO: Implement user API call
          console.warn('User resource fetching not implemented yet');
          return [];
          
        case 'TICKET':
          // TODO: Implement ticket API call
          console.warn('Ticket resource fetching not implemented yet');
          return [];
          
        case 'CUSTOMER':
          // TODO: Implement customer API call
          console.warn('Customer resource fetching not implemented yet');
          return [];
          
        default:
          console.warn(`Unknown resourceId: ${resourceId}`);
          return [];
      }
    } catch (error) {
      console.error(`Error fetching resource values for ${resourceId}:`, error);
      return [];
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            underline="hover" 
            color="inherit" 
            href="/" 
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Início
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Notifications sx={{ mr: 0.5 }} fontSize="inherit" />
            Catálogo de Notificações
          </Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Catálogo de Notificações
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Gerencie templates de notificação, configurações canônicas e mapeamentos de eventos.
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Enhanced Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Tabs 
            value={selectedTab} 
            onChange={onTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                py: 3,
                minHeight: 72
              },
              '& .Mui-selected': {
                bgcolor: 'background.paper'
              }
            }}
          >
            <Tab 
              label="Notificações Canônicas" 
              icon={<Box component="span" sx={{ fontSize: '1.2rem', mb: 0.5 }}>📋</Box>}
              iconPosition="top"
            />
            <Tab 
              label="Templates" 
              icon={<Box component="span" sx={{ fontSize: '1.2rem', mb: 0.5 }}>📝</Box>}
              iconPosition="top"
            />
            <Tab 
              label="Mapeamento" 
              icon={<Box component="span" sx={{ fontSize: '1.2rem', mb: 0.5 }}>🔗</Box>}
              iconPosition="top"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 0 }}>
          {selectedTab === 0 && (
            <CanonicalNotificationsTab 
              setupTemplates={setupTemplates} 
              messageTemplates={messageTemplates}
              isLoading={isLoading}
              fetchFieldCatalog={fetchFieldCatalog}
              fetchResourceValues={fetchResourceValues}
            />
          )}
          {selectedTab === 1 && <TemplatesTab messageTemplates={messageTemplates} isLoading={isLoading} />}
          {selectedTab === 2 && <MappingTab isLoading={isLoading} />}
        </Box>
      </Paper>
    </Container>
  )
}

export default NotificationCatalogView
